import { useEffect, useRef } from 'react';
import type { PlatformTheme } from '@platzona/platform-theme';

type UsePlatformThemeBootstrapOptions = {
  loadAndApplyTheme: () => Promise<PlatformTheme>;
  isAuthenticated: () => boolean;
};

/**
 * Loads the active theme on mount and re-syncs when login/logout flips.
 * Token refresh also fires `platzona-auth-change` — ignore those so CSS vars
 * are not reapplied (which looks like a full-shell color flicker).
 */
export function usePlatformThemeBootstrap({
  loadAndApplyTheme,
  isAuthenticated,
}: UsePlatformThemeBootstrapOptions): void {
  const loadRef = useRef(loadAndApplyTheme);
  const isAuthedRef = useRef(isAuthenticated);

  useEffect(() => {
    loadRef.current = loadAndApplyTheme;
    isAuthedRef.current = isAuthenticated;
  });

  useEffect(() => {
    void loadRef.current();
  }, []);

  useEffect(() => {
    let wasAuthed = isAuthedRef.current();

    const sync = () => {
      const authed = isAuthedRef.current();
      if (authed === wasAuthed) return;
      wasAuthed = authed;
      void loadRef.current();
    };

    window.addEventListener('platzona-auth-change', sync);
    return () => window.removeEventListener('platzona-auth-change', sync);
  }, []);
}
